import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const TimeLogModal = ({ isOpen, onClose, order, onSaveTimeLog }) => {
  const [timeData, setTimeData] = useState({
    startTime: '',
    endTime: '',
    description: '',
    breakTime: 0
  });
  const [isTimerActive, setIsTimerActive] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Timer effect
  useEffect(() => {
    let interval;
    if (isTimerActive) {
      interval = setInterval(() => {
        setCurrentTime(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isTimerActive]);

  // Initialize with current time when modal opens
  useEffect(() => {
    if (isOpen && order) {
      const now = new Date();
      const timeString = now?.toTimeString()?.slice(0, 5);
      setTimeData(prev => ({
        ...prev,
        startTime: timeString
      }));
      setCurrentTime(0);
      setIsTimerActive(false);
    }
  }, [isOpen, order]);

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours?.toString()?.padStart(2, '0')}:${minutes?.toString()?.padStart(2, '0')}:${secs?.toString()?.padStart(2, '0')}`;
  };

  const calculateDuration = () => {
    if (!timeData?.startTime || !timeData?.endTime) return 0;
    
    const start = new Date(`2024-01-01T${timeData.startTime}`);
    const end = new Date(`2024-01-01T${timeData.endTime}`);
    
    if (end <= start) return 0;
    
    const diffMs = end - start;
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    return Math.max(0, diffMinutes - (timeData?.breakTime || 0));
  };

  const handleStartTimer = () => {
    const now = new Date();
    const timeString = now?.toTimeString()?.slice(0, 5);
    setTimeData(prev => ({
      ...prev,
      startTime: timeString
    }));
    setCurrentTime(0);
    setIsTimerActive(true);
  };

  const handleStopTimer = () => {
    const now = new Date();
    const timeString = now?.toTimeString()?.slice(0, 5);
    setTimeData(prev => ({
      ...prev,
      endTime: timeString
    }));
    setIsTimerActive(false);
  };

  const handleInputChange = (field, value) => {
    setTimeData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!timeData?.startTime) {
      newErrors.startTime = 'Horário de início é obrigatório';
    }
    if (!timeData?.endTime) {
      newErrors.endTime = 'Horário de fim é obrigatório';
    }
    if (timeData?.startTime && timeData?.endTime) {
      const start = new Date(`2024-01-01T${timeData.startTime}`);
      const end = new Date(`2024-01-01T${timeData.endTime}`);
      if (end <= start) {
        newErrors.endTime = 'Horário de fim deve ser posterior ao início';
      }
    }
    if (!timeData?.description?.trim()) {
      newErrors.description = 'Descrição das atividades é obrigatória';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);
    
    try {
      const duration = calculateDuration();
      const timeLog = {
        id: Date.now(),
        orderId: order?.id,
        date: new Date()?.toISOString()?.split('T')?.[0],
        startTime: timeData?.startTime,
        endTime: timeData?.endTime,
        duration,
        breakTime: timeData?.breakTime || 0,
        description: timeData?.description,
        createdAt: new Date()?.toISOString()
      };

      await onSaveTimeLog(timeLog);
      handleClose();
    } catch (error) {
      console.error('Error saving time log:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setTimeData({
      startTime: '',
      endTime: '',
      description: '',
      breakTime: 0
    });
    setErrors({});
    setIsTimerActive(false);
    setCurrentTime(0);
    onClose();
  };

  if (!isOpen || !order) return null;

  const duration = calculateDuration();

  return (
    <div className="fixed inset-0 z-[1000] bg-background/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg shadow-modal w-full max-w-lg">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
              <Icon name="Clock" size={20} className="text-success" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-card-foreground">
                Registro de Tempo
              </h2>
              <p className="text-sm text-muted-foreground">
                OS #{order?.number} - {order?.client?.name}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Timer Display */}
        {isTimerActive && (
          <div className="p-4 bg-success/5 border-b border-border">
            <div className="text-center">
              <div className="text-2xl font-mono font-bold text-success mb-2">
                {formatTime(currentTime)}
              </div>
              <div className="flex items-center justify-center space-x-2 text-sm text-success">
                <div className="w-2 h-2 bg-success rounded-full animate-pulse"></div>
                <span>Timer ativo desde {timeData?.startTime}</span>
              </div>
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-4">
            {/* Timer Controls */}
            <div className="flex justify-center space-x-3 mb-6">
              {!isTimerActive ? (
                <Button
                  type="button"
                  variant="success"
                  onClick={handleStartTimer}
                  iconName="Play"
                  iconPosition="left"
                >
                  Iniciar Timer
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleStopTimer}
                  iconName="Pause"
                  iconPosition="left"
                >
                  Parar Timer
                </Button>
              )}
            </div>

            {/* Time Inputs */}
            <div className="grid grid-cols-2 gap-4">
              <Input
                type="time"
                label="Horário de Início"
                value={timeData?.startTime}
                onChange={(e) => handleInputChange('startTime', e?.target?.value)}
                error={errors?.startTime}
                required
                disabled={isSubmitting || isTimerActive}
              />
              <Input
                type="time"
                label="Horário de Fim"
                value={timeData?.endTime}
                onChange={(e) => handleInputChange('endTime', e?.target?.value)}
                error={errors?.endTime}
                required
                disabled={isSubmitting || isTimerActive}
              />
            </div>

            {/* Break Time */}
            <Input
              type="number"
              label="Tempo de Pausa (minutos)"
              placeholder="0"
              value={timeData?.breakTime}
              onChange={(e) => handleInputChange('breakTime', parseInt(e?.target?.value) || 0)}
              min="0"
              disabled={isSubmitting}
            />

            {/* Duration Display */}
            {duration > 0 && (
              <div className="p-3 bg-muted/50 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Duração Total:</span>
                  <span className="font-semibold text-card-foreground">
                    {Math.floor(duration / 60)}h {duration % 60}min
                  </span>
                </div>
              </div>
            )}

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-card-foreground mb-2">
                Descrição das Atividades *
              </label>
              <textarea
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                rows={4}
                placeholder="Descreva as atividades realizadas durante este período..."
                value={timeData?.description}
                onChange={(e) => handleInputChange('description', e?.target?.value)}
                disabled={isSubmitting}
              />
              {errors?.description && (
                <p className="text-sm text-error mt-1">{errors?.description}</p>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 p-6 border-t border-border bg-muted/30">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              loading={isSubmitting}
              iconName="Save"
              iconPosition="left"
              disabled={isTimerActive}
            >
              {isSubmitting ? 'Salvando...' : 'Salvar Registro'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TimeLogModal;